import React, { useEffect, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Home from './pages/Home';
import EventDetail from './components/EventDetail';
import EventMap from './pages/EventMap';
import { AuthProvider, useAuth } from './components/Login/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import Login from './components/Login/Login';
import './utils/axiosConfig';
import ProtectedRoute from './components/Login/ProtectedRoute';
import { startLangDecayTimer, stopLangDecayTimer } from './services/langPrefs';
import { useBehaviorTracking } from './hooks/useBehaviorTracking';
import DynamicAppMeta from './components/pwa/DynamicAppMeta';
import QRScanner from './components/Header/QRScanner';
import { recordNavigation } from './utils/navHistory';
import BackHandlerProvider from './components/common/BackHandlerProvider';
import useDeepLinks from './hooks/useDeepLinks';

// Lazy-loaded routes — not needed on initial Home page load
const EventInputPage = React.lazy(() => import('./components/InputPages/EventInputPage'));
const ReviewInputPage = React.lazy(() => import('./components/InputPages/ReviewInputPage'));
const CouponInputPage = React.lazy(() => import('./components/InputPages/CouponInputPage'));
const ApprovalPage = React.lazy(() => import('./components/approval/ApprovalPage'));
const ReviewApprovalPage = React.lazy(() => import('./components/approval/ReviewApprovalPage'));
const EditEventPage = React.lazy(() => import('./EditEventPage'));
const SubmittedList = React.lazy(() => import('./SubmittedList'));
const EventUpload = React.lazy(() => import('./components/EventUpload/EventUpload'));
const EventListPage = React.lazy(() => import('./EventListPage'));
const Profile = React.lazy(() => import('./components/Profile/ProfilePage'));
const Dashboard = React.lazy(() => import('./components/Dashboard/Dashboard'));
const UserProfile = React.lazy(() => import('./components/Profile/UserProfile'));
const MyProfilePage = React.lazy(() => import('./components/Profile/MyProfilePage'));
const EventGroupsList = React.lazy(() => import('./components/groups/EventGroupsList'));
const EventGroupChat = React.lazy(() => import('./components/groups/EventGroupChat'));
const ChatPage = React.lazy(() => import('./components/Dashboard/UserDashboard/ChatPage'));
const TourGuideDashboard = React.lazy(() => import('./components/TourGuide/TourGuideDashboard'));
const ShareTour = React.lazy(() => import('./components/TourGuide/ShareTour'));
const TourGuideMap = React.lazy(() => import('./components/TourGuide/TourGuideMap'));
const JoinTour = React.lazy(() => import('./components/TourGuide/JoinTour'));
const TourParticipant = React.lazy(() => import('./components/TourGuide/TourParticipant'));
const ChangePassword = React.lazy(() => import('./pages/ChangePassword'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const VenueOwnerDashboard = React.lazy(() => import('./components/Dashboard/VenueOwnerDashboard'));
const MyCoupons = React.lazy(() => import('./components/coupon/MyCoupons'));
const CouponRedeemPage = React.lazy(() => import('./components/coupon/CouponRedeemPage'));
const AnalyticsDashboard = React.lazy(() => import('./pages/admin/AnalyticsDashboard'));
const UserBehaviorDashboard = React.lazy(() => import('./pages/admin/UserBehaviorDashboard'));
const FindDuplicatesPage = React.lazy(() => import('./pages/admin/FindDuplicatesPage'));
const AdminUsersPage = React.lazy(() => import('./pages/admin/AdminUsersPage'));
const AutoApprovalPage = React.lazy(() => import('./pages/admin/AutoApprovalPage'));
const ReportManagementPage = React.lazy(() => import('./pages/admin/ReportManagementPage'));
const RClipperVideosPage = React.lazy(() => import('./pages/admin/RClipperVideosPage'));
const AdminReviewsPage = React.lazy(() => import('./pages/admin/AdminReviewsPage'));
const AdminVenuesPage = React.lazy(() => import('./pages/admin/AdminVenuesPage'));
const AdminLayout = React.lazy(() => import('./components/Dashboard/AdminLayout'));
const AdminDashboard = React.lazy(() => import('./components/Dashboard/AdminDashboard'));
const ManagePostsPage = React.lazy(() => import('./pages/ManagePostsPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/Terms/TermsOfServicePage'));
const DeleteAccountPage = React.lazy(() => import('./pages/DeleteAccountPage'));
const SavedItemsPage = React.lazy(() => import('./pages/SavedItemsPage'));

// Create a separate component for the routes that need access to auth context
const AppRoutes = () => {
  const { user } = useAuth(); // Get user from AuthContext
  const { language } = useLanguage(); // Get language from LanguageContext
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state && location.state.background ? location.state : null;

  // Remember the previous in-app route so on-screen Back/Home controls can
  // decide between popping the history stack and pushing a fresh route.
  // Cheap: a string compare + one sessionStorage write per navigation.
  useEffect(() => {
    recordNavigation(location.pathname);
  }, [location.pathname]);

  // Native only: turn app.travy.android://line-callback?code=… (sent by the
  // bridge page after LINE Login) into a /login?code=… navigation so the app,
  // not the browser, finishes the sign-in.
  useDeepLinks();

  // Global session tracking (3.1.1)
  const { tracker } = useBehaviorTracking('app');
  const sessionStartedRef = useRef(false);

  // Track session start once on mount
  useEffect(() => {
    if (!tracker) return;

    // Prevent double-start if AppRoutes re-renders
    if (!sessionStartedRef.current) {
      tracker.trackSessionStart('home');
      sessionStartedRef.current = true;
    }

    // Handle session end on unmount
    return () => {
      try {
        tracker.trackSessionEnd('unmount');
      } catch (err) {
        console.error('Error ending behavior session on unmount:', err);
      }
    };
  }, [tracker]);

  // Handle session end on tab close/unload
  useEffect(() => {
    if (!tracker) return;

    const handleBeforeUnload = () => {
      try {
        tracker.trackSessionEnd('unload');
      } catch (err) {
        console.error('Error ending session before unload:', err);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [tracker]);

  return (
    <Suspense fallback={<div className="loading-container"><div className="loading-spinner" /></div>}>
      {/* Primary routes; when background exists, render them using it */}
      <Routes location={state?.background || location}>
      {/* Public routes that don't require authentication */}
      <Route path="/login" element={<Login />} />
      <Route path="/aboutus" element={<Profile />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/home" element={<Home />} />
      <Route path="/event/:id" element={<EventDetail />} />
      <Route path="/map/:id" element={<EventMap />} />
      <Route path="/eventinput" element={<EventInputPage />} />
      <Route path="/review_input" element={
        <ProtectedRoute>
          <ReviewInputPage />
        </ProtectedRoute>
      } />
      <Route path="/coupon_input" element={<CouponInputPage />} />
      <Route path="/submittedlist" element={<SubmittedList />} />
      <Route path="/qr-scanner" element={<QRScanner />} />
      <Route path="/join-tour/scan" element={<QRScanner />} />

      {/* FIXED: JoinTour should be public so people can see tour details before logging in */}
      <Route path="/join-tour/:token" element={<JoinTour />} />
 
      {/* Protected routes that require authentication */}
      <Route path="/event-list" element={
        <ProtectedRoute>
          <EventListPage />
        </ProtectedRoute>
      } />
      
      <Route path="/approval" element={
        <ProtectedRoute requiredRole="admin">
          <ApprovalPage />
        </ProtectedRoute>
      } />

      <Route path="/review-approval" element={
        <ProtectedRoute requiredRole="admin">
          <ReviewApprovalPage />
        </ProtectedRoute>
      } />

      {/* ── Admin console ───────────────────────────────────────────────
          Every screen below is a *child* of <AdminLayout>, which renders the
          admin header and the left menu once and drops the routed page into
          its right-hand pane via <Outlet />. Selecting a menu item therefore
          swaps only that pane — the header and menu stay mounted instead of
          the browser loading a whole new page.

          Pages shared with non-admin users (event input, approval, event
          list, edit, upload) keep their original top-level route for those
          users and additionally appear here under an /admin/... alias, which
          is what the admin menu links to. */}
      <Route
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />

        {/* Events */}
        <Route path="/admin/event-input" element={<EventInputPage />} />
        <Route path="/admin/approval" element={<ApprovalPage />} />
        <Route path="/admin/review-approval" element={<ReviewApprovalPage />} />
        <Route path="/admin/auto-approval" element={<AutoApprovalPage />} />
        <Route path="/admin/event-list" element={<EventListPage />} />
        <Route path="/admin/event-edit" element={<EditEventPage />} />
        <Route path="/admin/event-upload" element={<EventUpload />} />
        <Route path="/admin/rclipper-videos" element={<RClipperVideosPage />} />

        {/* Data management */}
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/role-requests" element={<AdminUsersPage />} />
        <Route path="/admin/reviews" element={<AdminReviewsPage />} />
        <Route path="/admin/venues" element={<AdminVenuesPage />} />

        {/* Insights */}
        <Route path="/admin/analytics" element={<AnalyticsDashboard />} />
        <Route path="/admin/behavior" element={<UserBehaviorDashboard />} />
        <Route path="/admin/report-management" element={<ReportManagementPage />} />
        <Route path="/admin/find-duplicates" element={<FindDuplicatesPage />} />
      </Route>

      <Route path="/finddub" element={<FindDuplicatesPage />} />

      <Route path="/editpage" element={
        <ProtectedRoute>
          <EditEventPage />
        </ProtectedRoute>
      } />
           
      <Route path="/event-upload" element={
        <ProtectedRoute>
          <EventUpload />
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />

      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePassword language={language} />
        </ProtectedRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute>
          <MyProfilePage />
        </ProtectedRoute>
      } />
      
      {/* Fixed ChatPage route with proper user data */}
      <Route path="/chat/:conversationId" element={
        <ProtectedRoute>
          <ChatPage user={user} />
        </ProtectedRoute>
      } />
      
      {/* New Group Chat Routes */}
      <Route path="/event/:eventId/groups" element={
        <ProtectedRoute>
          <EventGroupsList />
        </ProtectedRoute>
      } />
      
      <Route path="/groups/:groupId/chat" element={
        <ProtectedRoute>
          <EventGroupChat />
        </ProtectedRoute>
      } />

      <Route path="/profile/:userId" element={
        <ProtectedRoute>
          <UserProfile />
        </ProtectedRoute>
      } />

      {/* Tour Guide Routes - Protected except for JoinTour */}
      <Route path="/tour-guide" element={
        <ProtectedRoute>
          <TourGuideDashboard user={user} />
        </ProtectedRoute>
      } />
      
      <Route path="/tour-guide/share/:tourId" element={
        <ProtectedRoute>
          <ShareTour user={user} />
        </ProtectedRoute>
      } />
      
      <Route path="/tour-guide/map/:tourId" element={
        <ProtectedRoute>
          <TourGuideMap user={user} />
        </ProtectedRoute>
      } />
      
      {/* TourParticipant requires authentication since user is already joined */}
      <Route path="/tour-participant/:tourId" element={
        <ProtectedRoute>
          <TourParticipant user={user} />
        </ProtectedRoute>
      } />

      <Route path="/manage-posts" element={
        <ProtectedRoute>
          <ManagePostsPage />
        </ProtectedRoute>
      } />

      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/saved" element={
        <ProtectedRoute>
          <SavedItemsPage />
        </ProtectedRoute>
      } />

      <Route path="/venue/dashboard" element={
        <ProtectedRoute>
          <VenueOwnerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/c/:token" element={<CouponRedeemPage />} />
      <Route path="/my-coupons" element={
        <ProtectedRoute>
          <MyCoupons />
        </ProtectedRoute>
      } />

      {/* Root path - redirect to home */}
      <Route path="/" element={<Home />} />
      
      {/* Catch-all for unmatched routes */}
      <Route path="*" element={
        <div className="not-found">
          <h1>404 - ไม่พบหน้าที่คุณกำลังค้นหา</h1>
          <p>กลับไปยัง <a href="/">หน้าหลัก</a></p>
        </div>
      } />
      </Routes>

      {/* Secondary layer: modal overlay rendering only when background exists */}
      {state?.background && (
        <Routes>
          <Route
            path="/login"
            element={
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 9999,
                  overflowY: 'auto',
                }}
              >
                <Login />
              </div>
            }
          />
          {/* Dashboard as an overlay when it was opened from Home.
              Home stays mounted in the primary layer underneath, so closing
              this reveals it exactly as the user left it — no remount, no
              refetch, no map re-initialisation. Opened via
              utils/dashboardNavigation.openDashboard. */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <div
                  style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9998,
                    background: '#f5f5f5',
                    overflowY: 'auto'
                  }}
                >
                  <Dashboard onBack={() => navigate(-1)} />
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tour-guide"
            element={
              <ProtectedRoute>
                <div
                  style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9998,
                    background: '#f5f5f5',
                    overflowY: 'auto'
                  }}
                >
                  <TourGuideDashboard user={user} />
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tour-guide/share/:tourId"
            element={
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Share tour"
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,0.35)',
                  zIndex: 9999,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  padding: '24px 12px'
                }}
                onClick={(e) => {
                  // click outside to close
                  if (e.target === e.currentTarget) window.history.back();
                }}
              >
                <div
                  style={{
                    width: 'min(100%, 980px)',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    borderRadius: 16,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                    background: 'var(--panel, #fff)'
                  }}
                >
                  <ShareTour user={user} />
                </div>
              </div>
            }
          />
        </Routes>
      )}
    </Suspense>
  );
};

const App = () => {
  useEffect(() => {
    startLangDecayTimer();
    return () => stopLangDecayTimer();
  }, []);

  return (
    <Router>
      <DynamicAppMeta />
      <LanguageProvider>
        <AuthProvider>
          {/* Owns the Android hardware back button for the whole app. Must sit
              inside Router + AuthProvider: it reads the current location and
              needs isAuthenticated to pick a fallback on deep links. Inert on
              web — see components/common/BackHandlerProvider.jsx. */}
          <BackHandlerProvider>
            <AppRoutes />
          </BackHandlerProvider>
        </AuthProvider>
      </LanguageProvider>
    </Router>
  );
};

export default App;